export type FileKind = "image" | "video" | "pdf" | "spreadsheet" | "word" | "other";

export function fileExtension(filename: string): string {
  const i = filename.lastIndexOf(".");
  return i === -1 ? "" : filename.slice(i + 1).toLowerCase();
}

export function formatFileSize(bytes: number): string {
  if (!bytes || bytes < 0) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
}

export function fileTypeLabel(filename: string): string {
  return fileExtension(filename).toUpperCase() || "FILE";
}

export function fileKind(filename: string, contentType?: string | null): FileKind {
  const ext = fileExtension(filename);
  if (contentType?.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) {
    return "image";
  }
  if (contentType?.startsWith("video/") || ["mp4", "mov"].includes(ext)) return "video";
  if (contentType === "application/pdf" || ext === "pdf") return "pdf";
  if (["csv", "xls", "xlsx"].includes(ext)) return "spreadsheet";
  if (["doc", "docx"].includes(ext)) return "word";
  return "other";
}

export function isPreviewableFile(filename: string, contentType?: string | null): boolean {
  const kind = fileKind(filename, contentType);
  return kind === "image" || kind === "pdf";
}

const UNKNOWN_MIME_TYPES = new Set(["", "application/octet-stream"]);

function acceptTokens(accept: string): string[] {
  return accept.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
}

function mimeMatches(mime: string, tokens: string[]): boolean {
  return tokens.some((t) => t === mime || (t.endsWith("/*") && mime.startsWith(t.slice(0, -1))));
}

export function acceptsMimeType(mime: string, accept: string): boolean | null {
  const type = mime.toLowerCase();
  if (UNKNOWN_MIME_TYPES.has(type)) return null;
  return mimeMatches(type, acceptTokens(accept));
}

export function acceptsFile(file: File, accept: string): boolean {
  const tokens = acceptTokens(accept);
  const name = file.name.toLowerCase();
  if (tokens.some((t) => t.startsWith(".") && name.endsWith(t))) return true;
  return mimeMatches(file.type.toLowerCase(), tokens);
}
